import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';

import { ThunkExtra } from '@app/store';
import {
  DashboardService,
  GetAccountWidgetCreateOptionsResponse,
  GetAccountWidgetPartitionsResponse,
  UpdateWidgetPartitionsRequest,
  UpdateWidgetPartitionsParams,
  GetAccountWidgetPartitionsParams,
  GetAccountWidgetCreateOptionsParams,
  GetAccountWidgetPartitionsQuery,
  UpdateWidgetPartitionsQuery,
} from '@app/gen/dashboard';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { SettingsState } from '../../../settings.state';

const MIN_GRID_COLS = 2;

const getWidgetPartitions = createAsyncThunk<
  GetAccountWidgetPartitionsResponse,
  {
    params: GetAccountWidgetPartitionsParams;
    query?: GetAccountWidgetPartitionsQuery;
  },
  ThunkExtra
>(
  'settings/getWidgetPartitions',
  async ({ params, query }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      return await service.getAccountWidgetPartitions({
        params,
        query,
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const getAvailablePartitions = createAsyncThunk<
  GetAccountWidgetCreateOptionsResponse,
  { params: GetAccountWidgetCreateOptionsParams },
  ThunkExtra
>(
  'settings/getAvailablePartitions',
  async ({ params }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      return await service.getAccountWidgetCreateOptions({
        params,
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getPartitionsState = createAsyncThunk<
  undefined,
  GetAccountWidgetPartitionsParams & GetAccountWidgetPartitionsQuery,
  ThunkExtra
>(
  'settings/getPartitionsState',
  async (
    { account_id, widget_id, company_id },
    { rejectWithValue, dispatch },
  ) => {
    const body = {
      params: {
        account_id,
        widget_id,
      },
      ...(company_id && {
        query: {
          company_id,
        },
      }),
    };

    try {
      await Promise.all([
        awaitDispatch(dispatch(getWidgetPartitions(body))),
        awaitDispatch(dispatch(getAvailablePartitions(body))),
      ]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateWidgetPartitions = createAsyncThunk<
  void,
  {
    query?: UpdateWidgetPartitionsQuery;
    params: UpdateWidgetPartitionsParams;
    body: UpdateWidgetPartitionsRequest;
  },
  ThunkExtra
>(
  'settings/updateWidgetPartitions',
  async ({ params, body, query }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      await service.updateWidgetPartitions({
        query,
        params,
        body,
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsPartitionsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getWidgetPartitions.fulfilled, (state, { payload }) => {
    state.partitions.items = payload.partitions;
    state.partitions.cols = Math.max(payload?.grid?.cols || 1, MIN_GRID_COLS);
    state.partitions.rows = payload?.grid?.rows || 1;
    state.partitions.isException = !!payload.company_content_id;
  });
  builder.addCase(getAvailablePartitions.fulfilled, (state, { payload }) => {
    state.partitions.availablePartitions = payload.availablePartitions || [];
    state.partitions.requiredPartitions = payload.requiredPartitions || [];
  });
  builder.addCase(getPartitionsState.pending, (state) => {
    state.partitions.status = 'loading';
    state.partitions.error = null;
  });
  builder.addCase(getPartitionsState.fulfilled, (state) => {
    state.partitions.status = 'complete';
    state.partitions.error = null;
  });
  builder.addCase(getPartitionsState.rejected, (state, { payload, error }) => {
    state.partitions.status = 'failed';
    state.partitions.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
